import React from "react";
import type { SceneLayout } from "./frame";
import type { Theme } from "./theme";
import { ramp } from "./motion";
import { Background, Kicker, Headline, Donut, Caption, type CaptionSeg } from "./prims";

/**
 * Anivaze v4 — hand-authored demo scenes (Phase 1 proof).
 *
 * Each scene is a PURE function of (t, layout, theme). It lays itself out
 * against `layout` (never raw pixels) and colours itself from `theme`, so the
 * SAME scene reflows to 9:16 / 1:1 / 16:9 and re-renders across every theme with
 * zero changes. This is what the AI Director will emit later. See PLAN §8.
 */

export interface SceneCtx {
  t: number;
  dur: number;
  layout: SceneLayout;
  theme: Theme;
}

export interface SceneDef {
  id: string;
  label: string;
  format: string;
  dur: number;
  render: (ctx: SceneCtx) => React.ReactNode;
  /**
   * Optional CAMERA track (camera.ts). When a scene lays its content out in a
   * "world" larger than the frame (long flow diagram, wide chart, Ken-Burns
   * still), it supplies keyframes here and the Stage pans/zooms/scrolls across
   * it as a function of `t`. Absent → identity view (no camera, no regression).
   */
  camera?: import("./camera").CameraTrack;
}

// ─── Explainer hook: "Why your brain craves sugar" ────────────────────────
const explainerHook: SceneDef = {
  id: "explainer-sugar",
  label: "Explainer hook",
  format: "Explainer",
  dur: 6.4,
  render: ({ t, layout, theme }) => {
    const { content, caption, hero, unit } = layout;
    const sweep = ramp(t, 0.5, 1.4, theme.motion);
    const segs: CaptionSeg[] = [
      { text: "Your brain runs on glucose", start: 0.3, end: 2.3 },
      { text: "sugar is its fastest fuel", start: 2.3, end: 4.3 },
      { text: "so it rewards every hit", start: 4.3, end: 6.4 },
    ];
    return (
      <>
        <Kicker text="How your body works" x={content.cx} y={content.y + unit * 1.4} theme={theme} unit={unit} />
        <Headline
          lines={["Why your brain", "craves sugar"]}
          x={content.cx}
          yTop={content.y + unit * 4.2}
          sizeUnit={7.6}
          theme={theme}
          unit={unit}
          t={t}
        />
        <Donut cx={hero.cx} cy={hero.cy + unit * 3} r={unit * 12} value={0.74} sweep={sweep} theme={theme} unit={unit} role="positive" />
        <Caption box={caption} segs={segs} t={t} theme={theme} unit={unit} />
      </>
    );
  },
};

// ─── Motivational quote ───────────────────────────────────────────────────
const quoteScene: SceneDef = {
  id: "quote-discipline",
  label: "Motivational quote",
  format: "Quote",
  dur: 5.6,
  render: ({ t, layout, theme }) => {
    const { content, caption, unit } = layout;
    const segs: CaptionSeg[] = [
      { text: "between what you want now", start: 0.4, end: 2.8 },
      { text: "and what you want most", start: 2.8, end: 5.6 },
    ];
    return (
      <>
        <Kicker text="Mindset" x={content.cx} y={content.y + unit * 1.6} theme={theme} unit={unit} />
        <Headline
          lines={["Discipline", "is choosing"]}
          x={content.cx}
          yTop={content.cy - unit * 6}
          sizeUnit={9.5}
          theme={theme}
          unit={unit}
          t={t}
        />
        <Caption box={caption} segs={segs} t={t} theme={theme} unit={unit} />
      </>
    );
  },
};

export const SCENES: SceneDef[] = [explainerHook, quoteScene];
export { Background };
